import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { HOLIDAYS } from '../../../constants/holiday';
import { getRegions } from '../../../atoms/regions';
import { useRecoilValue } from 'recoil';
import { getHolidaySettingsByRegion, updateHolidaySettingsByRegion } from '../../../repositories/HttpRepository';
import { toast } from 'react-toastify';
import { ToastMessage } from '../../../constants/toastMessage';
import CloseIcon from '@mui/icons-material/Close';

const ALL_REGIONS_OPTION = {
  label: 'All Regions',
  value: 'DEFAULT',
};

const renderRegionsInput = (params) => <TextField {...params} label="Regions" />;

const HolidaySettingsModal = ({ open, onClose }) => {
  const regions = useRecoilValue(getRegions);
  const [selectedRegion, setSelectedRegion] = useState(ALL_REGIONS_OPTION);
  const [saving, setSaving] = useState(false);
  const [settingsData, setSettingsData] = useState({});
  const [originalData, setOriginalData] = useState({}); // Store original fetched data for comparison
  const [loading, setLoading] = useState(false);
  const [pendingRegion, setPendingRegion] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  // Helper function to compare two arrays of settings
  const hasDataChanged = (currentData, originalData) => {
    if (!originalData) return true; // If no original data, consider it changed
    if (currentData.length !== originalData.length) return true;

    // Create maps for easier comparison
    const currentMap = new Map(currentData.map((item) => [item.key, item.off]));
    const originalMap = new Map(originalData.map((item) => [item.key, item.off]));

    // Check if any key has different value or if keys are different
    if (currentMap.size !== originalMap.size) return true;

    for (const [key, value] of currentMap) {
      if (originalMap.get(key) !== value) return true;
    }

    return false;
  };

  const hasUnsavedChanges = settingsData[selectedRegion?.value] && hasDataChanged(settingsData[selectedRegion?.value], originalData[selectedRegion?.value]);

  const fetchHolidaySettingsByRegion = async (region) => {
    if (settingsData[region]) {
      return;
    }
    try {
      setLoading(true);

      const res = await getHolidaySettingsByRegion(region);
      if (res.ok && res.data) {
        const data = res.data || [];
        setSettingsData((prev) => ({
          ...prev,
          [region]: data,
        }));
        // Store original data for comparison
        setOriginalData((prev) => ({
          ...prev,
          [region]: JSON.parse(JSON.stringify(data)), // Deep copy
        }));
      }
    } catch (error) {
      // console.error('Failed to fetch holiday settings:', error);
      toast.error(error.message || ToastMessage.FETCH_FAIL);
    } finally {
      setLoading(false);
    }
  };

  const onChangeSettings = (holidayKey, checked) => {
    if (!selectedRegion?.value) return;

    setSettingsData((prev) => {
      const regionData = prev[selectedRegion.value] || [];
      const existingIndex = regionData.findIndex((item) => item.key === holidayKey);

      let updatedRegionData;
      if (existingIndex >= 0) {
        // Update existing entry
        updatedRegionData = [...regionData];
        updatedRegionData[existingIndex] = { ...updatedRegionData[existingIndex], off: checked };
      } else {
        // Add new entry
        updatedRegionData = [...regionData, { key: holidayKey, off: checked }];
      }

      return {
        ...prev,
        [selectedRegion.value]: updatedRegionData,
      };
    });
  };

  const handleRegionChange = (event, option) => {
    if (hasUnsavedChanges) {
      setPendingRegion(option);
      setConfirmationOpen(true);
    } else {
      setSelectedRegion(option);
    }
  };

  const handleDiscardChanges = () => {
    // Revert changes for current region
    const region = selectedRegion.value;
    setSettingsData((prev) => ({
      ...prev,
      [region]: JSON.parse(JSON.stringify(originalData[region] || [])),
    }));

    // Switch to pending region
    if (pendingRegion) {
      setSelectedRegion(pendingRegion);
      setPendingRegion(null);
    }

    setConfirmationOpen(false);
  };

  const handleSaveChanges = async () => {
    try {
      if (!selectedRegion?.value) return;

      setConfirmationOpen(false);
      setSaving(true);
      const region = selectedRegion.value;
      const data = settingsData[region];

      const res = await updateHolidaySettingsByRegion(region, data);

      if (res.ok) {
        setOriginalData((prev) => ({
          ...prev,
          [region]: JSON.parse(JSON.stringify(data)),
        }));

        // Switch to pending region
        if (pendingRegion) {
          setSelectedRegion(pendingRegion);
          setPendingRegion(null);
        }

        toast.success('Updated successfully for ' + region, { autoClose: 5000 });
      } else {
        const errorMessages = res.data?.error || ToastMessage.UPDATED_FAIL;
        toast.error(errorMessages);
      }
    } catch (error) {
      // console.error('Failed to save holiday settings:', error);
      toast.error(error.message || ToastMessage.UPDATED_FAIL);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSwitchRegion = () => {
    setPendingRegion(null);
    setConfirmationOpen(false);
  };

  useEffect(() => {
    if (selectedRegion?.value && open) {
      fetchHolidaySettingsByRegion(selectedRegion.value);
    }
  }, [selectedRegion, open]);

  const styles = selectedRegion?.value && !loading ? {} : { opacity: 0 };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          transition: {
            onExited: () => {
              setSelectedRegion(ALL_REGIONS_OPTION);
              setSettingsData({});
              setOriginalData({});
              setLoading(false);
            },
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            Holiday Settings
            <IconButton onClick={onClose} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack sx={{ gap: 1 }}>
            <Autocomplete options={[ALL_REGIONS_OPTION, ...regions]} renderInput={renderRegionsInput} size="small" onChange={handleRegionChange} value={selectedRegion} disableClearable />
            <Stack sx={{ gap: 1 }}>
              <Typography sx={styles} variant="body2">
                Select holidays to exclude for booking sessions applying for{' '}
                <Typography component="span" sx={{ color: 'warning.main' }}>
                  {selectedRegion?.label}
                </Typography>
              </Typography>
              <FormGroup sx={styles}>
                {HOLIDAYS.map((holiday) => {
                  const isChecked = Boolean(settingsData?.[selectedRegion?.value]?.find((date) => date?.key === holiday?.key)?.off);
                  return <FormControlLabel control={<Checkbox />} label={holiday.label} key={holiday.key} checked={isChecked} onChange={(e) => onChangeSettings(holiday.key, e.target.checked)} />;
                })}
              </FormGroup>
              {loading && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%' }} />}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDiscardChanges} color="inherit" disabled={saving || !hasUnsavedChanges}>
            Discard
          </Button>
          <Button onClick={handleSaveChanges} variant="contained" color="primary" disabled={saving || !hasUnsavedChanges}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmationOpen} onClose={handleCancelSwitchRegion} maxWidth="300">
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes for <b>{selectedRegion?.label}</b>. Need to save or discard them before switching.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSwitchRegion} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDiscardChanges} color="error">
            Discard
          </Button>
          <Button onClick={handleSaveChanges} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HolidaySettingsModal;
